import { faArrowRight, faCalendar, faMapMarkerAlt, faUserFriends } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Col, List } from 'antd';
import { FormattedMessage } from 'react-intl';

import { LotEventType } from '@/types/enums';
import { getFormattedDate } from '@/utils/moment';
import { getTranslatedString } from '@/utils';

export const columns = [
  {
    title: <FormattedMessage id="lots.events.id" />,
    dataIndex: 'id',
  },
  {
    title: <FormattedMessage id="lots.events.type" />,
    dataIndex: 'type',
  },
  {
    title: <FormattedMessage id="lots.events.namespace" />,
    dataIndex: 'namespace',
  },
  {
    title: <FormattedMessage id="lots.events.comment" />,
    dataIndex: 'comment',
  },
  {
    title: <FormattedMessage id="lots.events.date" />,
    dataIndex: 'createdAt',
    sorter: true,
    render: getFormattedDate,
  },
];

const UPDATE_EVENT_HIDDEN_KEYS = new Set(['isDraft']);

/** One-way format: primitive or unwrap { new } / { old, new }; never show [object Object]. */
const formatEventValue = (v: unknown): string => {
  if (v == null) return '—';
  if (typeof v === 'object') {
    const o = v as Record<string, unknown>;
    if ('new' in o) return formatEventValue(o.new);
    if ('old' in o) return formatEventValue(o.old);
    if (typeof o.name === 'string') return o.name;
    if (o.id != null) return String(o.id);
    return '—';
  }
  if (typeof v === 'string') {
    if (/^\d{4}-\d{2}-\d{2}T/.test(v)) return getFormattedDate(v) ?? v;
    return v;
  }
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return '—';
};

export const getExpandedRow = ({ metadata, type }: cyrian.api.lots.Event) => {
  const UpdateRenderItem = (fieldName: string) => {
    if (UPDATE_EVENT_HIDDEN_KEYS.has(fieldName)) return null;
    const value = metadata[fieldName];
    if (value == null) return null;
    const o = value as Record<string, unknown>;
    const hasOld = o && typeof o === 'object' && 'old' in o;
    const hasNew = o && typeof o === 'object' && 'new' in o;
    if (hasOld || hasNew) {
      return (
        <List.Item key={fieldName} title={fieldName}>
          <Col span={6}>{fieldName}:</Col>
          <Col span={6}>{formatEventValue(hasOld ? o.old : undefined)}</Col>
          <Col span={6}>
            <FontAwesomeIcon icon={faArrowRight} />
          </Col>
          <Col span={6}>{formatEventValue(hasNew ? o.new : undefined)}</Col>
        </List.Item>
      );
    }
    return (
      <List.Item key={fieldName} title={fieldName}>
        <Col span={6}>{fieldName}:</Col>
        <Col span={18}>{formatEventValue(value)}</Col>
      </List.Item>
    );
  };
  const AnalysisRenderItem = (
    <List.Item>
      <Col span={8}>
        <FontAwesomeIcon icon={faMapMarkerAlt} /> {metadata.address}
      </Col>
      <Col span={8}>
        <FontAwesomeIcon icon={faCalendar} /> {getFormattedDate(metadata.date)}
      </Col>
      <Col span={8}>
        <FontAwesomeIcon icon={faUserFriends} /> {metadata.counterparty}
      </Col>
    </List.Item>
  );
  const WeighingRenderItem = (
    <List.Item>
      <Col span={12}>
        <FontAwesomeIcon icon={faUserFriends} /> {metadata.counterparty}
      </Col>
      <Col span={12}>Weight: {metadata.weight}</Col>
    </List.Item>
  );
  const BvRenderItem = (
    <List.Item>
      <Col span={8}>{metadata.oldStatus}</Col>
      <Col span={8}>
        <FontAwesomeIcon icon={faArrowRight} />
      </Col>
      <Col span={8}>{metadata.newStatus}</Col>
    </List.Item>
  );
  const FumigationRenderItem = (
    <List.Item>
      <Col span={8}>
        <FontAwesomeIcon icon={faMapMarkerAlt} /> {metadata.address}
      </Col>
      <Col span={8}>
        <FontAwesomeIcon icon={faCalendar} /> Start - {getFormattedDate(metadata.startDate)}
      </Col>
      <Col span={8}>
        <FontAwesomeIcon icon={faCalendar} /> End - {getFormattedDate(metadata.endDate)}
      </Col>
    </List.Item>
  );
  const ProposalRenderItem = (
    <List.Item>
      {Boolean(metadata.old?.contract) && (
        <Col span={5}>
          <FontAwesomeIcon icon={faUserFriends} /> {metadata.old?.contract}
        </Col>
      )}
      {Boolean(metadata.old?.status) && <Col span={5}>Status: {metadata.old?.status}</Col>}
      <Col span={4}>
        <FontAwesomeIcon icon={faArrowRight} />
      </Col>
      {Boolean(metadata.new?.contract) && (
        <Col span={5}>
          <FontAwesomeIcon icon={faUserFriends} /> {metadata.new?.contract}
        </Col>
      )}
      {Boolean(metadata.new?.status) && (
        <Col span={5}>{getTranslatedString(`proposal.status.${metadata.new?.status}`)}</Col>
      )}
    </List.Item>
  );

  switch (type) {
    case LotEventType.Analysis: {
      return AnalysisRenderItem;
    }
    case LotEventType.Weighing: {
      return WeighingRenderItem;
    }
    case LotEventType.BV: {
      return BvRenderItem;
    }
    case LotEventType.Fumigation: {
      return FumigationRenderItem;
    }
    case LotEventType.Proposal: {
      return ProposalRenderItem;
    }
    case LotEventType.Update: {
      const displayKeys = Object.keys(metadata || {}).filter((key) => !UPDATE_EVENT_HIDDEN_KEYS.has(key));
      return <List dataSource={displayKeys} renderItem={(key) => UpdateRenderItem(key)} />;
    }
    default:
      return JSON.stringify(metadata);
  }
};
